import { useState } from 'react'
import { Button } from '../ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { CursorPagination, type PageInfo, CryptoIcon } from '../ui'
import { Check, Flag, FlagOff, X } from 'lucide-react'
import { type TransactionData } from './TransactionsTabView'
import { ellipsise } from '../../utils/strings'
import { formatDate, getNetworkName } from '../../lib/transaction-utils'
import { SkeletonTable } from '../skeleton/Skeleton'
import { ExternalApiBadge, FlaggedBadge, StatusBadge, TransactionTypeBadge } from '../badges/status'
import { ApproveConfirmationDialog } from '../modals/ApproveConfirmationDialog'
import { RejectConfirmationDialog } from '../modals/RejectConfirmationDialog'

export interface TransactionsTableProps {
  transactions: TransactionData[]
  loading?: boolean
  onApproveTransaction?: (transactionId: string) => void
  onRejectTransaction?: (transactionId: string) => void
  onViewTransactionDetails?: (transactionId: string, type: string) => void
  showActionButtons?: boolean
  showFlagColumn?: boolean
  hideDepositActions?: boolean
  hideGroupColumn?: boolean
  isComplianceUser?: boolean

  // Pagination props
  pageInfo?: PageInfo
  totalCount?: number
  onNextPage?: (cursor: string) => void
  onPreviousPage?: (cursor: string) => void
  showPagination?: boolean
}

export function TransactionsTable({
  transactions,
  loading = false,
  onApproveTransaction,
  onRejectTransaction,
  onViewTransactionDetails,
  showActionButtons = false,
  showFlagColumn = false,
  hideDepositActions = false,
  hideGroupColumn = false,
  isComplianceUser = false,
  // Pagination props
  pageInfo,
  totalCount = 0,
  onNextPage,
  onPreviousPage,
  showPagination = false,
}: TransactionsTableProps) {
  // Modal states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null)

  // Handler functions for modals
  const showApproveDialog = (transactionId: string) => {
    setSelectedTransactionId(transactionId)
    setApproveDialogOpen(true)
  }

  const showRejectDialog = (transactionId: string) => {
    setSelectedTransactionId(transactionId)
    setRejectDialogOpen(true)
  }

  const handleApproveConfirm = async () => {
    if (!selectedTransactionId || !onApproveTransaction) return

    try {
      await onApproveTransaction(selectedTransactionId)
      setApproveDialogOpen(false)
      setSelectedTransactionId(null)
    } catch (_error) {
      // Error handling done by parent
    }
  }

  const handleRejectConfirm = async () => {
    if (!selectedTransactionId || !onRejectTransaction) return

    try {
      await onRejectTransaction(selectedTransactionId)
      setRejectDialogOpen(false)
      setSelectedTransactionId(null)
    } catch (_error) {
      // Error handling done by parent
    }
  }

  const canApproveRejectTransaction = (trade: TransactionData) => {
    if (trade.depositStatus === 'INITIATED' || trade.swapStatus === 'REQUESTED') {
      return true
    }
    return false
  }

  if (loading) {
    return <SkeletonTable />
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="h-[60px]">
              {showFlagColumn && <TableHead className="w-[50px]">Flag</TableHead>}
              <TableHead className="w-[100px]">ID</TableHead>
              {!hideGroupColumn && <TableHead>Group</TableHead>}
              <TableHead>Company</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Amounts</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              {showActionButtons && <TableHead className="w-[100px]"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.map((trade, index) => (
              <TableRow key={`${trade.id}-${index}`} className="h-[60px]">
                {showFlagColumn && (
                  <TableCell>
                    <div className="h-8 w-8 p-0 flex items-center justify-center">
                      {trade.isFlagged ? (
                        <Flag className="h-4 w-4 text-red-500 fill-red-500" />
                      ) : (
                        <FlagOff className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </TableCell>
                )}
                <TableCell className="font-medium">{ellipsise(trade.id, 20)}</TableCell>
                {!hideGroupColumn && <TableCell className="font-medium">{trade.group}</TableCell>}
                <TableCell className="font-medium">{trade.company}</TableCell>
                <TableCell>{trade.type === 'TRADE' ? trade.user : ''}</TableCell>
                <TableCell>
                  <TransactionTypeBadge type={trade.type} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {trade.type === 'TRADE' ? (
                      <>
                        <span>
                          <CryptoIcon
                            symbol={trade.transaction.fromCurrency || ''}
                            imageOverride={trade.transaction.fromCurrencyImage || ''}
                          />
                        </span>
                        <span className="text-gray-500">{trade.transaction.from}</span>
                        <div className="flex flex-col justify-center">
                          <span className="text-xs leading-none">
                            {trade.transaction.fromCurrency}
                          </span>
                          <span className="text-xs text-gray-400 leading-none">
                            {getNetworkName(
                              trade.transaction.fromNetwork || '',
                              trade.transaction.fromCurrency || '',
                            )}
                          </span>
                        </div>
                        <span className="text-gray-400">→</span>

                        <span>
                          <CryptoIcon
                            symbol={trade.transaction.toCurrency || ''}
                            imageOverride={trade.transaction.toCurrencyImage || ''}
                          />
                        </span>
                        <span className="text-gray-500">{trade.transaction.to}</span>
                        <div className="flex flex-col justify-center">
                          <span className="text-xs leading-none">
                            {trade.transaction.toCurrency}
                          </span>
                          <span className="text-xs text-gray-400 leading-none">
                            {getNetworkName(
                              trade.transaction.toNetwork || '',
                              trade.transaction.toCurrency || '',
                            )}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <span>
                          <CryptoIcon
                            symbol={trade.transaction.currency || ''}
                            imageOverride={trade.transaction.fromCurrencyImage || ''}
                          />
                        </span>
                        <span className="text-gray-500">{trade.transaction.amount}</span>

                        <div className="flex flex-col justify-center">
                          <span className="text-xs leading-none">{trade.transaction.currency}</span>
                          <span className="text-xs text-gray-400 leading-none">
                            {getNetworkName(
                              trade.transaction.fromNetwork || '',
                              trade.transaction.currency || '',
                            )}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <StatusBadge
                      status={trade.swapStatus || trade.depositStatus || trade.status || ''}
                      type={trade.type}
                    />
                    <FlaggedBadge flagged={trade.isFlagged} />
                    <ExternalApiBadge isExternalApi={trade.isExternalApi} />
                  </div>
                </TableCell>
                <TableCell>
                  {formatDate(trade.finalizedDate || trade.approvedDate || trade.createdDate!)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    {canApproveRejectTransaction(trade) && showActionButtons && (
                      <>
                        {onApproveTransaction &&
                          (!hideDepositActions || trade.type === 'TRADE') &&
                          (!isComplianceUser || trade.type === 'DEPOSIT') && (
                            <Button
                              variant="main_action"
                              size="sm"
                              className=""
                              onClick={() => showApproveDialog(trade.id)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                        {onRejectTransaction &&
                          (!hideDepositActions || trade.type === 'TRADE') &&
                          (!isComplianceUser || trade.type === 'DEPOSIT') && (
                            <Button
                              variant="main_outline"
                              size="sm"
                              className="hover:bg-main/5"
                              onClick={() => showRejectDialog(trade.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                      </>
                    )}
                    {onViewTransactionDetails && (
                      <Button
                        variant="main_outline"
                        size="sm"
                        onClick={() => onViewTransactionDetails(trade.id, trade.type)}
                      >
                        View details
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {showPagination && pageInfo && onNextPage && onPreviousPage && (
        <div className="mt-4">
          <CursorPagination
            pageInfo={pageInfo}
            totalCount={totalCount}
            loading={loading}
            onNext={onNextPage}
            onPrevious={onPreviousPage}
          />
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      <ApproveConfirmationDialog
        open={approveDialogOpen}
        title="Approve Transaction"
        description="Are you sure you want to approve this transaction? This action cannot be undone."
        onOpenChange={setApproveDialogOpen}
        onConfirm={handleApproveConfirm}
      />
      <RejectConfirmationDialog
        open={rejectDialogOpen}
        title="Reject Transaction"
        description="Are you sure you want to reject this transaction? This action cannot be undone."
        onOpenChange={setRejectDialogOpen}
        onConfirm={handleRejectConfirm}
      />
    </div>
  )
}
